import {
  expandRemovedStepIdsForDataClear,
  stripFormFieldsForRemovedSteps,
} from './requestScopeChange';
import {
  isOptionalRequestFormStepId,
  type OptionalRequestFormStepId,
} from '../config/requestFormStepRegistry';
import { coolingSystemConfigurationsKeys } from '../hooks/queries/useCoolingSystemConfigurations';
import { largeUpsConfigurationsKeys } from '../hooks/queries/useLargeUpsConfigurations';
import { liquidCoolingCduConfigurationsKeys } from '../hooks/queries/useLiquidCoolingCduConfigurations';
import { powerDistributionConfigurationsKeys } from '../hooks/queries/usePowerDistributionConfigurations';
import { rackConfigurationsKeys } from '../hooks/queries/useRackConfigurations';
import { requestsKeys } from '../hooks/queries/useRequestsQuery';
import { smallUpsConfigurationsKeys } from '../hooks/queries/useSmallUpsConfigurations';
import { mirrorCoolingSystemToOfflineRequestCache } from '../services/coolingSystemOfflineSync';
import { mirrorLargeUpsToOfflineRequestCache } from '../services/largeUpsOfflineSync';
import { mirrorLiquidCoolingCduToOfflineRequestCache } from '../services/liquidCoolingCduOfflineSync';
import { mirrorPowerDistributionToOfflineRequestCache } from '../services/powerDistributionOfflineSync';
import { mirrorRacksToOfflineRequestCache } from '../services/rackOfflineSync';
import { mirrorSmallUpsToOfflineRequestCache } from '../services/smallUpsOfflineSync';

import type { QueryClient } from '@tanstack/react-query';

type GroupedRepeatableCache = {
  existing: unknown[];
  new: unknown[];
};

const EMPTY_GROUPED_REPEATABLE: GroupedRepeatableCache = {
  existing: [],
  new: [],
};

type RepeatableScopeClearTarget = {
  discardEditor?: () => void;
  resetSession?: () => void;
  keys: {
    list: (id: string) => readonly unknown[];
    grouped: (id: string) => readonly unknown[];
  };
  clearOfflineMirror?: (requestId: string) => Promise<void>;
};

const REPEATABLE_SCOPE_CLEAR_BY_STEP: Partial<
  Record<OptionalRequestFormStepId, RepeatableScopeClearTarget>
> = {
  'rack-power': {
    keys: rackConfigurationsKeys,
    clearOfflineMirror: requestId =>
      mirrorRacksToOfflineRequestCache(requestId, []),
  },
  'small-ups': {
    keys: smallUpsConfigurationsKeys,
    clearOfflineMirror: requestId =>
      mirrorSmallUpsToOfflineRequestCache(requestId, []),
  },
  'large-ups': {
    keys: largeUpsConfigurationsKeys,
    clearOfflineMirror: requestId =>
      mirrorLargeUpsToOfflineRequestCache(requestId, []),
  },
  'power-distribution': {
    keys: powerDistributionConfigurationsKeys,
    clearOfflineMirror: requestId =>
      mirrorPowerDistributionToOfflineRequestCache(requestId, []),
  },
  'cooling-readiness': {
    keys: coolingSystemConfigurationsKeys,
    clearOfflineMirror: requestId =>
      mirrorCoolingSystemToOfflineRequestCache(requestId, []),
  },
  'liquid-cooling': {
    keys: liquidCoolingCduConfigurationsKeys,
    clearOfflineMirror: requestId =>
      mirrorLiquidCoolingCduToOfflineRequestCache(requestId, []),
  },
};

type DiscardEditorHandle = {
  discardOpenRackEditor?: () => void;
  resetRackSessionAfterScopeRemoval?: () => void;
  discardOpenSmallUpsEditor?: () => void;
  resetSmallUpsSessionAfterScopeRemoval?: () => void;
  discardOpenLargeUpsEditor?: () => void;
  resetLargeUpsSessionAfterScopeRemoval?: () => void;
  discardOpenPowerDistributionEditor?: () => void;
  resetPowerDistributionSessionAfterScopeRemoval?: () => void;
  discardOpenCoolingSystemEditor?: () => void;
  resetCoolingSystemSessionAfterScopeRemoval?: () => void;
  discardOpenLiquidCoolingCduEditor?: () => void;
  resetLiquidCoolingCduSessionAfterScopeRemoval?: () => void;
};

export type ScopeRemovalSideEffectsParams = {
  queryClient: QueryClient;
  requestId?: string;
  removedStepIds: readonly string[];
  nextStoredStepIds?: string[] | null;
  stage?: string | null;
  handles?: DiscardEditorHandle;
  onSessionDirtyClear?: (stepId: OptionalRequestFormStepId) => void;
  /**
   * Default true. False when the removal is deferred to the next Save
   * (#1501 aggregate save): the server still holds the units, so the query
   * caches and offline mirrors must survive for Cancel. The Save calls
   * `purgeScopeRemovalCaches` once the server has cleared the scope.
   */
  purgeCaches?: boolean;
};

export type ScopeRemovalCachePurgeParams = Pick<
  ScopeRemovalSideEffectsParams,
  'queryClient' | 'requestId' | 'removedStepIds' | 'nextStoredStepIds' | 'stage'
>;

function purgeListAndGrouped(
  queryClient: QueryClient,
  requestId: string,
  keys: RepeatableScopeClearTarget['keys'],
): void {
  const listKey = keys.list(requestId);
  const groupedKey = keys.grouped(requestId);
  queryClient.removeQueries({ queryKey: listKey });
  queryClient.removeQueries({ queryKey: groupedKey });
  queryClient.setQueryData(listKey, []);
  queryClient.setQueryData(groupedKey, EMPTY_GROUPED_REPEATABLE);
}

function resolveRepeatableScopeClearTarget(
  stepId: OptionalRequestFormStepId,
  handles?: DiscardEditorHandle,
): RepeatableScopeClearTarget | undefined {
  const base = REPEATABLE_SCOPE_CLEAR_BY_STEP[stepId];
  if (!base) {
    return undefined;
  }

  switch (stepId) {
    case 'rack-power':
      return {
        ...base,
        discardEditor: handles?.discardOpenRackEditor,
        resetSession: handles?.resetRackSessionAfterScopeRemoval,
      };
    case 'small-ups':
      return {
        ...base,
        discardEditor: handles?.discardOpenSmallUpsEditor,
        resetSession: handles?.resetSmallUpsSessionAfterScopeRemoval,
      };
    case 'large-ups':
      return {
        ...base,
        discardEditor: handles?.discardOpenLargeUpsEditor,
        resetSession: handles?.resetLargeUpsSessionAfterScopeRemoval,
      };
    case 'power-distribution':
      return {
        ...base,
        discardEditor: handles?.discardOpenPowerDistributionEditor,
        resetSession: handles?.resetPowerDistributionSessionAfterScopeRemoval,
      };
    case 'cooling-readiness':
      return {
        ...base,
        discardEditor: handles?.discardOpenCoolingSystemEditor,
        resetSession: handles?.resetCoolingSystemSessionAfterScopeRemoval,
      };
    case 'liquid-cooling':
      return {
        ...base,
        discardEditor: handles?.discardOpenLiquidCoolingCduEditor,
        resetSession: handles?.resetLiquidCoolingCduSessionAfterScopeRemoval,
      };
    default:
      return base;
  }
}

/**
 * Drop the removed scopes' child rows from the request detail cache, the
 * list/grouped queries and the offline mirrors, so a later refetch cannot
 * re-attach them as ghosts (#997, #1542).
 */
export async function purgeScopeRemovalCaches(
  params: ScopeRemovalCachePurgeParams,
): Promise<void> {
  const expanded = expandRemovedStepIdsForDataClear(
    params.removedStepIds.filter(isOptionalRequestFormStepId),
    params.nextStoredStepIds,
  );

  if (expanded.length === 0 || !params.requestId) {
    return;
  }
  const requestId = params.requestId;

  params.queryClient.setQueryData(
    requestsKeys.detail(requestId),
    (current: Record<string, unknown> | undefined) => {
      if (!current) {
        return current;
      }
      return stripFormFieldsForRemovedSteps(
        current,
        params.removedStepIds,
        params.nextStoredStepIds,
        params.stage,
      );
    },
  );

  for (const stepId of expanded) {
    const target = REPEATABLE_SCOPE_CLEAR_BY_STEP[stepId];
    if (!target) {
      continue;
    }
    purgeListAndGrouped(params.queryClient, requestId, target.keys);
    await target.clearOfflineMirror?.(requestId);
  }
}

/** Discard open editors and purge child-table caches after scope removal (#997). */
export async function applyScopeRemovalSideEffects(
  params: ScopeRemovalSideEffectsParams,
): Promise<void> {
  const expanded = expandRemovedStepIdsForDataClear(
    params.removedStepIds.filter(isOptionalRequestFormStepId),
    params.nextStoredStepIds,
  );

  if (expanded.length === 0) {
    return;
  }

  for (const stepId of expanded) {
    params.onSessionDirtyClear?.(stepId);

    const target = resolveRepeatableScopeClearTarget(stepId, params.handles);
    target?.discardEditor?.();
    target?.resetSession?.();
  }

  if (params.purgeCaches !== false) {
    await purgeScopeRemovalCaches(params);
  }
}
